import type { StoredSession } from './storage';

/**
 * The engaged time after which a page load sends its first session_update.
 * Each page view carries this value, so a report of a visit that has no
 * session checkpoint can say the visit was shorter than this.
 */
export const FIRST_SESSION_UPDATE_MS = 5_000;
const SESSION_UPDATE_STEP_MS = 2_000;
const MAX_SESSION_UPDATE_MS = 30_000;

/**
 * The wait before the next session_update of a page load: 5 seconds before
 * the first, then 2 seconds more each time, 30 seconds at most. A short visit
 * gets a checkpoint early, and a long one sends fewer than a fixed 15 second
 * rhythm would.
 */
export const sessionUpdateDelay = (updatesSent: number): number => Math.min(
  MAX_SESSION_UPDATE_MS,
  FIRST_SESSION_UPDATE_MS + SESSION_UPDATE_STEP_MS * Math.max(0, updatesSent)
);

export type SessionCheckpointReason =
  | 'background'
  | 'interval'
  | 'pagehide'
  | 'stop'
  | 'timeout';

export type SessionCheckpoint = {
  kind: 'end' | 'update';
  sessionId: string;
  sessionReferrer: StoredSession['referrer'];
  occurredAt: number;
  activeMs: number;
  sessionSpanMs: number;
  reason: SessionCheckpointReason;
};

type CreateSession = (now: number) => StoredSession;

export class SessionClock {
  private engagedSince: number | null;
  /** The engaged time of the stored record this tab last read or wrote; the rest is this tab's own. */
  private savedActiveMs: number;

  constructor(
    private record: StoredSession,
    private readonly timeoutMs: number,
    private readonly createSession: CreateSession,
    initiallyEngaged: boolean,
    now: number
  ) {
    this.engagedSince = initiallyEngaged ? now : null;
    this.savedActiveMs = record.activeMs;
  }

  get sessionId(): string {
    return this.record.id;
  }

  get sessionReferrer(): StoredSession['referrer'] {
    return this.record.referrer;
  }

  /**
   * Takes in the session record that the other tabs of the browser stored.
   * Call it before each change. The latest activity of any tab keeps the
   * session alive and the engaged time of the tabs adds up. A newer session
   * that another tab started replaces this tab's session.
   */
  syncShared(shared: StoredSession | null, now: number): void {
    if (!shared) return;
    const unsavedActiveMs = Math.max(0, this.record.activeMs - this.savedActiveMs);
    if (shared.id === this.record.id) {
      this.record = {
        ...this.record,
        startedAt: Math.min(this.record.startedAt, shared.startedAt),
        lastActivityAt: Math.max(this.record.lastActivityAt, shared.lastActivityAt),
        activeMs: shared.activeMs + unsavedActiveMs,
        referrer: this.record.referrer === undefined ? shared.referrer : this.record.referrer
      };
      this.savedActiveMs = shared.activeMs;
      return;
    }
    if (shared.lastActivityAt > this.record.lastActivityAt && now - shared.lastActivityAt < this.timeoutMs) {
      this.record = { ...shared };
      this.savedActiveMs = shared.activeMs;
    }
  }

  /** States that the current record is stored. */
  markSaved(): void {
    this.savedActiveMs = this.record.activeMs;
  }

  get snapshot(): StoredSession {
    return { ...this.record };
  }

  recordActivity(now: number): SessionCheckpoint[] {
    const expired = this.rotateIfExpired(now);
    this.record.lastActivityAt = now;
    return expired ? [expired] : [];
  }

  setEngaged(engaged: boolean, now: number): SessionCheckpoint[] {
    if (engaged === (this.engagedSince !== null)) return [];

    const expired = this.rotateIfExpired(now);
    if (engaged) {
      this.engagedSince = now;
      this.record.lastActivityAt = now;
      return expired ? [expired] : [];
    }

    if (expired) {
      this.engagedSince = null;
      return [expired];
    }

    this.accrueActiveTime(now);
    this.engagedSince = null;
    this.record.lastActivityAt = now;
    return [this.createCheckpoint('update', 'background', now)];
  }

  checkpoint(now: number): SessionCheckpoint[] {
    if (this.engagedSince === null) return [];

    const expired = this.rotateIfExpired(now);
    if (expired) return [expired];

    this.accrueActiveTime(now);
    this.record.lastActivityAt = now;
    return [this.createCheckpoint('update', 'interval', now)];
  }

  end(reason: 'pagehide' | 'stop', now: number): SessionCheckpoint[] {
    const expired = this.rotateIfExpired(now);
    if (expired) {
      this.engagedSince = null;
      return [expired];
    }

    this.accrueActiveTime(now);
    this.engagedSince = null;
    this.record.lastActivityAt = now;
    return [this.createCheckpoint('end', reason, now)];
  }

  private accrueActiveTime(now: number): void {
    if (this.engagedSince === null) return;
    this.record.activeMs += Math.max(0, now - this.engagedSince);
    this.engagedSince = now;
  }

  private rotateIfExpired(now: number): SessionCheckpoint | null {
    if (now - this.record.lastActivityAt < this.timeoutMs) return null;

    const expired = this.createCheckpoint('end', 'timeout', this.record.lastActivityAt);
    const wasEngaged = this.engagedSince !== null;
    this.record = this.createSession(now);
    this.savedActiveMs = this.record.activeMs;
    this.engagedSince = wasEngaged ? now : null;
    return expired;
  }

  private createCheckpoint(
    kind: SessionCheckpoint['kind'],
    reason: SessionCheckpointReason,
    occurredAt: number
  ): SessionCheckpoint {
    return {
      kind,
      sessionId: this.record.id,
      sessionReferrer: this.record.referrer,
      occurredAt,
      activeMs: Math.round(this.record.activeMs),
      sessionSpanMs: Math.max(0, occurredAt - this.record.startedAt),
      reason
    };
  }
}
